/**
 * informantFormKeys.js
 *
 * Resolves which questionnaire keys an informant has been assigned, given their
 * assessment pathway and (where the pathway cares) their role.
 *
 * WHY THIS EXISTS
 * ---------------
 * Adult Autism was the first modular pathway, so code that needed "the list of
 * informant forms" imported AUTISM_INFORMANT_FORM_KEYS directly. That is a
 * hidden assumption: it silently returns autism form keys for every informant,
 * whatever pathway they are on.
 *
 * For a CYP ADHD informant that assumption fails closed in a very visible way —
 * none of the autism keys exist in their form set, so the dashboard would list
 * zero questionnaires and progress would compute as 0/7 forever. This module is
 * the single place that maps (module, role) -> form keys, so adding a future
 * pathway means editing one function rather than hunting down every consumer.
 *
 * Keep in sync with the Python registries:
 *   - adult_autism_informant/inclusion.py
 *   - cyp_adhd/registry.py
 */

import { AUTISM_INFORMANT_FORM_KEYS } from './adultAutismFormKeys';
import { getCypAdhdFormKeysForRole } from './cypAdhdFormKeys';

/**
 * Resolve the ordered form keys assigned to a modular informant.
 *
 * @param {string} assessmentModule - 'adultAutism' | 'cypAdhd'
 * @param {string} [informantRole]  - 'legalGuardian' | 'schoolRep' (CYP only)
 * @returns {string[]} ordered form keys; empty array for unknown modules
 */
export function getInformantFormKeys(assessmentModule, informantRole) {
  if (assessmentModule === 'cypAdhd') {
    return getCypAdhdFormKeysForRole(informantRole);
  }
  if (assessmentModule === 'adultAutism') {
    return AUTISM_INFORMANT_FORM_KEYS;
  }
  // Unknown/non-modular module: return empty rather than guessing. Callers
  // render "no questionnaires" instead of a misleading autism form list.
  return [];
}
